import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../../styles/DriverLicenseScanPreview.css';

const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

export interface DLTransaction {
  transactionId: string;
  fileName: string;
  created: string;
  userName: string;
  status: string;
  responseJson?: string;
  submittedRequestJson?: string;
  selectionOptionsJson?: string;
  isLiked?: boolean | null;
  isUpdateInNowcerts?: boolean;
  totalCount?: number;
}

interface DriverFields {
  firstName: string; lastName: string; dateOfBirth: string;
  drivingLicenseNumber: string; issueDate: string; expirationDate: string;
  addressLine1: string; city: string; state: string; zipCode: string;
  drivingLicenseClass: string; endorsements: string; restrictions: string;
  sex: string; height: string; weight: string; eyeColor: string; hairColor: string;
}

const EMPTY: DriverFields = {
  firstName:'', lastName:'', dateOfBirth:'', drivingLicenseNumber:'',
  issueDate:'', expirationDate:'', addressLine1:'', city:'',
  state:'', zipCode:'', drivingLicenseClass:'', endorsements:'',
  restrictions:'', sex:'', height:'', weight:'', eyeColor:'', hairColor:'',
};

const parseFields = (tx: DLTransaction): DriverFields => {
  try {
    if (tx.isUpdateInNowcerts && tx.submittedRequestJson) {
      const r = JSON.parse(tx.submittedRequestJson);
      return {
        firstName:r.firstName||r.first_name||'', lastName:r.lastName||r.last_name||'',
        dateOfBirth:r.dateOfBirth||r.date_of_birth||'',
        drivingLicenseNumber:r.drivingLicenseNumber||r.license_number||'',
        issueDate:r.issueDate||r.issue_date||'', expirationDate:r.expirationDate||r.expiration_date||'',
        addressLine1:r.addressLine1||r.address_line_1||'', city:r.city||'',
        state:r.state||r.license_state||'', zipCode:r.zipCode||r.zip_code||'',
        drivingLicenseClass:r.drivingLicenseClass||r.driver_license_class||'',
        endorsements:r.endorsements||'', restrictions:r.restrictions||'',
        sex:r.sex||r.gender||'', height:r.height||'', weight:r.weight||'',
        eyeColor:r.eyeColor||r.eye_color||'', hairColor:r.hairColor||r.hair_color||'',
      };
    }
    if (tx.responseJson) {
      const r = JSON.parse(tx.responseJson);
      return {
        firstName:r.firstName||r.first_name||'', lastName:r.lastName||r.last_name||'',
        dateOfBirth:r.dateOfBirth||r.date_of_birth||'',
        drivingLicenseNumber:r.drivingLicenseNumber||r.license_number||'',
        issueDate:r.issueDate||r.issue_date||'', expirationDate:r.expirationDate||r.expiration_date||'',
        addressLine1:r.addressLine1||r.address_line_1||'', city:r.city||'',
        state:r.state||r.license_state||'', zipCode:r.zipCode||r.zip_code||'',
        drivingLicenseClass:r.drivingLicenseClass||r.driver_license_class||'',
        endorsements:r.endorsements||'', restrictions:r.restrictions||'',
        sex:r.sex||r.gender||'',
        height:r.heightFeet !== undefined && r.heightInch !== undefined
          ? `${r.heightFeet}.${r.heightInch}` : (r.height||''),
        weight:r.weight||'', eyeColor:r.eyeColor||r.eye_color||'',
        hairColor:r.hairColor||r.hair_color||'',
      };
    }
  } catch { /* ignore */ }
  return EMPTY;
};

const getClientInfo = (tx: DLTransaction) => {
  try {
    if (!tx.selectionOptionsJson) return { clientName: '—', insuredClientId: '' };
    const p = JSON.parse(tx.selectionOptionsJson);
    return { clientName: p.clientName || '—', insuredClientId: p.insuredClientId || '' };
  } catch { return { clientName: '—', insuredClientId: '' }; }
};

interface Props {
  tx: DLTransaction;
  onClose: () => void;
  onDriverAdded?: () => void;
  onUploadAms?: () => void;
  initialPdfUrl?: string;
  inline?: boolean;
  closeLabel?: string;
}

const DriverLicenseScanPreview: React.FC<Props> = ({
  tx, onClose, onDriverAdded, onUploadAms,
  initialPdfUrl, inline = false, closeLabel = '✕',
}) => {
  const disabled = !!tx.isUpdateInNowcerts;
  const { clientName, insuredClientId } = getClientInfo(tx);
  const init = parseFields(tx);

  const [pdfUrl,     setPdfUrl]     = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    if (initialPdfUrl) {
      setPdfUrl(initialPdfUrl);
      setPdfLoading(false);
      return;
    }
    setPdfLoading(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIDriverLicenseScan/previewdocument`,
      { transactionId: tx.transactionId, fileName: tx.fileName },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(res => setPdfUrl(res.data.fileUrl ?? null))
      .catch(() => {})
      .finally(() => setPdfLoading(false));
  }, [tx.transactionId, tx.fileName, initialPdfUrl]);

  const [firstName,            setFirstName]            = useState(init.firstName);
  const [lastName,             setLastName]             = useState(init.lastName);
  const [dateOfBirth,          setDateOfBirth]          = useState(init.dateOfBirth);
  const [drivingLicenseNumber, setDrivingLicenseNumber] = useState(init.drivingLicenseNumber);
  const [issueDate,            setIssueDate]            = useState(init.issueDate);
  const [expirationDate,       setExpirationDate]       = useState(init.expirationDate);
  const [addressLine1,         setAddressLine1]         = useState(init.addressLine1);
  const [city,                 setCity]                 = useState(init.city);
  const [state,                setState]                = useState(init.state);
  const [zipCode,              setZipCode]              = useState(init.zipCode);
  const [drivingLicenseClass,  setDrivingLicenseClass]  = useState(init.drivingLicenseClass);
  const [endorsements,         setEndorsements]         = useState(init.endorsements);
  const [restrictions,         setRestrictions]         = useState(init.restrictions);
  const [sex,                  setSex]                  = useState(init.sex);
  const [height,               setHeight]               = useState(init.height);
  const [weight,               setWeight]               = useState(init.weight);
  const [eyeColor,             setEyeColor]             = useState(init.eyeColor);
  const [hairColor,            setHairColor]            = useState(init.hairColor);

  const [addLoading, setAddLoading] = useState(false);
  const [addError,   setAddError]   = useState('');
  const [addSuccess, setAddSuccess] = useState(false);

  const onAddDriver = async () => {
    if (!insuredClientId) return;
    setAddLoading(true);
    setAddError('');
    try {
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/adddriver?ncToken=${getNcToken()}`,
        {
          insured_database_id:     insuredClientId,
          first_name:              firstName,
          last_name:               lastName,
          insured_commercial_name: clientName,
          license_number:          drivingLicenseNumber,
          driver_license_class:    drivingLicenseClass,
          date_of_birth:           dateOfBirth,
          gender:                  sex,
          license_state:           state,
          termination_date:        expirationDate,
          cdl_date:                issueDate,
          cdl_year:                issueDate.split('/')[2] || '',
          license_year:            issueDate.split('/')[2] || '',
          active:                  true,
          address_line_1:          addressLine1,
          driver_address_state:    state,
          city,
          zip_code:                zipCode,
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );

      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIDriverLicenseScan/updatehistory`,
        {
          TransactionId:        tx.transactionId,
          submittedRequestJson: JSON.stringify({
            firstName, lastName, dateOfBirth, drivingLicenseNumber, issueDate,
            expirationDate, addressLine1, city, state, zipCode,
            drivingLicenseClass, endorsements, restrictions, sex,
            height, weight, eyeColor, hairColor,
          }),
          selectionOptionsJson: JSON.stringify({ useExistingInsured: true, insuredClientId, clientName }),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );

      setAddSuccess(true);
      onDriverAdded?.();
    } catch (err: any) {
      setAddError(err.response?.data?.errorMessage || 'Error adding driver. Please try again.');
    } finally {
      setAddLoading(false);
    }
  };

  const isReadOnly = disabled || addSuccess;

  const fields: Array<{ label: string; value: string; setter: (v: string) => void }> = [
    { label: 'First Name',             value: firstName,            setter: setFirstName },
    { label: 'Last Name',              value: lastName,             setter: setLastName },
    { label: 'Date of Birth',          value: dateOfBirth,          setter: setDateOfBirth },
    { label: 'Driving License Number', value: drivingLicenseNumber, setter: setDrivingLicenseNumber },
    { label: 'Issue Date',             value: issueDate,            setter: setIssueDate },
    { label: 'Expiration Date',        value: expirationDate,       setter: setExpirationDate },
    { label: 'Address Line 1',         value: addressLine1,         setter: setAddressLine1 },
    { label: 'City',                   value: city,                 setter: setCity },
    { label: 'State',                  value: state,                setter: setState },
    { label: 'ZipCode',                value: zipCode,              setter: setZipCode },
    { label: 'Driving License Class',  value: drivingLicenseClass,  setter: setDrivingLicenseClass },
    { label: 'Endorsements',           value: endorsements,         setter: setEndorsements },
    { label: 'Restrictions',           value: restrictions,         setter: setRestrictions },
    { label: 'Sex',                    value: sex,                  setter: setSex },
    { label: 'Height',                 value: height,               setter: setHeight },
    { label: 'Weight',                 value: weight,               setter: setWeight },
    { label: 'Eye Color',              value: eyeColor,             setter: setEyeColor },
    { label: 'Hair Color',             value: hairColor,            setter: setHairColor },
  ];

  const panel = (
    <div
      className="driver-license-scan-preview__panel"
      style={{
        width: inline ? '100%' : '92vw',
        maxWidth: inline ? undefined : 1260,
        height: inline ? '100%' : '90vh',
        borderRadius: inline ? 0 : 12,
        boxShadow: inline ? 'none' : '0 20px 60px rgba(0,0,0,0.3)',
        border: inline ? 'none' : '1px solid var(--border-glass)',
      }}
    >

      {/* Top bar */}
      <div className="driver-license-scan-preview__topbar">
        {/* Left side */}
        {closeLabel !== '✕' ? (
          <button
            onClick={onClose}
            className="driver-license-scan-preview__back-btn"
          >
            ← {closeLabel}
          </button>
        ) : (
          <span className="driver-license-scan-preview__client-label">
            Client Name:{' '}
            <strong className="driver-license-scan-preview__client-name-accent">{clientName}</strong>
          </span>
        )}

        {/* Right side: close button for modal mode */}
        {closeLabel === '✕' && (
          <button
            onClick={onClose}
            className="driver-license-scan-preview__close-btn"
          >
            ✕
          </button>
        )}
      </div>

      {/* Body */}
      <div className="driver-license-scan-preview__body">

        {/* Left: document viewer */}
        <div className="cspr-pdf-panel">
          <div className="cspr-pdf-panel__header">
            <span className="cspr-pdf-panel__title">Uploaded Document</span>
            {!inline && onUploadAms && (
              <button
                className="cspr-action-btn cspr-action-btn--purple driver-license-scan-preview__upload-ams-btn"
                onClick={onUploadAms}
              >
                Upload In AMS
              </button>
            )}
          </div>
          <div className="cspr-pdf-viewer">
            {pdfUrl
              ? <iframe src={`${pdfUrl}`} title="DL Preview" className="driver-license-scan-preview__iframe" />
              : pdfLoading
                ? <div className="cspr-pdf-placeholder">Loading document…</div>
                : <div className="cspr-pdf-placeholder">Preview not available</div>
            }
          </div>
        </div>

        {/* Right: driver info */}
        <div className="driver-license-scan-preview__info-col">
          <div className="driver-license-scan-preview__info-scroll">

            {/* Card */}
            <div className="driver-license-scan-preview__card">
              {/* Card header */}
              <div className="driver-license-scan-preview__card-header">
                <span className="driver-license-scan-preview__card-title">
                  Driver Information
                </span>
                {disabled && (
                  <span className="driver-license-scan-preview__added-badge">
                    ✓ Driver Added in AMS
                  </span>
                )}
              </div>

              {/* Fields */}
              <div className="driver-license-scan-preview__fields">
                {addError && (
                  <p className="driver-license-scan-preview__add-error">{addError}</p>
                )}

                <div className="driver-license-scan-preview__fields-grid">
                  {fields.map(f => (
                    <div key={f.label}>
                      <label className="driver-license-scan-preview__field-label">
                        {f.label}
                      </label>
                      <input
                        type="text"
                        value={f.value}
                        disabled={isReadOnly}
                        onChange={e => f.setter(e.target.value)}
                        placeholder={`Enter ${f.label.toLowerCase()}`}
                        className="driver-license-scan-preview__field-input"
                        style={{
                          cursor: isReadOnly ? 'not-allowed' : 'text',
                          opacity: isReadOnly ? 0.65 : 1,
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="driver-license-scan-preview__footer">
                  {addSuccess ? (
                    <span className="driver-license-scan-preview__success-msg">
                      ✓ Driver added successfully!
                    </span>
                  ) : !disabled && (
                    <button
                      className="cspr-action-btn cspr-action-btn--purple"
                      disabled={addLoading || !insuredClientId}
                      onClick={onAddDriver}
                      style={{
                        opacity: (addLoading || !insuredClientId) ? 0.5 : 1,
                        cursor: (addLoading || !insuredClientId) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {addLoading ? 'Adding…' : 'Add Driver'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (inline) return panel;

  return ReactDOM.createPortal(
    <div
      className="csams-overlay driver-license-scan-preview__overlay-tint"
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()}>
        {panel}
      </div>
    </div>,
    document.body,
  );
};

export default DriverLicenseScanPreview;

